import React, { Component } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { getStyle, getModalStyle, finnishDateFormat } from '../../../../utils/utils';
import {
  updateVehicle as _updateVehicle,
  updateOrganizationVehicle as _updateOrganizationVehicle,
  fetchVehicle as _fetchVehicle,
} from '../../../../actions/vehicleActions';
import {
  fetchOrganizationVehicleSettingsChoices as _fetchOrganizationVehicleSettingsChoices,
} from '../../../../actions/organizationVehicleSettingsActions';
import CBSelectField from '../../../shared/form_components/CBSelectField';
import CBMediaQuery from '../../../shared/CBMediaQuery';
import CBButtonSubmit from '../../../shared/CBButtonSubmit';
import CBInputField from '../../../shared/form_components/CBInputField';
import { getLengthValidator } from '../../../../utils/validators';
import CBModal from '../../../shared/CBModal';
import CBSpinnerFloating from '../../../shared/CBSpinnerFloating';
import CBDatePickerInput from '../../../shared/CBDatePickerInput';
import moment from 'moment';
import { isEquipment } from '../../../../utils/equipment';

Modal.setAppElement('#root');

// Every key naming the vehicle has an equipment twin: a machine's dialog never
// says "ajoneuvo" or "auto".
const EQUIPMENT_KEYS = {
  vehicleTopInfoModalHeading: 'equipmentTopInfoModalHeading',
  organizationDataLabel: 'organizationEquipmentDataLabel',
  responsibleUser: 'equipmentResponsibleUser',
  noResponsibleUser: 'noEquipmentResponsibleUser',
  periodicMaintenanceInfo: 'equipmentPeriodicMaintenanceInfo',
};

class EditVehicleTopInfoModal extends Component {
  state = {
    formData: {
      year_model: '',
      owner_description: '',
      organization_data: {
        identifier: '',
        maintenance_interval_months: null,
        maintenance_interval_km: null,
        maintenance_interval_hours: null,
        business_unit: '',
        responsible_user: '',
      },
      last_periodic_maintenance: {
        date: '',
        km: null,
        hours: null,
        maintenance: null,
      },
      organization_vehicle_status: {
        employee: '',
      }
    },
    loading: false,
  };

  term = (key) => {
    const { t, vehicle } = this.props;
    return t(isEquipment(vehicle) && EQUIPMENT_KEYS[key] ? EQUIPMENT_KEYS[key] : key);
  };

  // Toimipiste / ajoneuvovastaava pick-lists are admin/manager data; everyone
  // else sees the current values as text.
  canManageSettings = () => {
    const { user, vehicle } = this.props;
    if (!user || !vehicle || !vehicle.organization_id) return false;
    if (user.is_staff) return true;
    const organization = (user.organizations || []).find((org) => org.id == vehicle.organization_id);
    return !!organization && (organization.user_role === 'admin' || organization.user_role === 'manager');
  };

  // Ajoneuvovastaava is a workshop chain care service concept, so a fleet that no
  // chain serves never sees it. The choices payload is the authority; the fleet
  // portal's own organization row answers before it lands (and for users who do
  // not fetch choices at all), and the vehicle's chain care service is the last
  // resort in the workshop portal, where the customer's organization is unknown.
  fleetBelongsToChain = () => {
    const { user, vehicle } = this.props;
    if (!vehicle) return false;
    const choices = this.getChoices();
    if (choices) return !!choices.has_chain_care_service;
    const organizations = (user && user.organizations) || [];
    const organization = organizations.find((org) => org.id == vehicle.organization_id);
    if (organization) {
      return !!(organization.workshop_chain_ids && organization.workshop_chain_ids.length);
    }
    return !!vehicle.chain_care_service_info;
  };

  componentDidMount = () => {
    const { vehicle, isOrganizationVehicle, fetchOrganizationVehicleSettingsChoices } = this.props;
    if (!vehicle) return;
    if (isOrganizationVehicle && this.canManageSettings()) {
      fetchOrganizationVehicleSettingsChoices(vehicle.organization_id);
    }
    if (vehicle.last_periodic_maintenance === undefined) {
      this.setState({ loading: true });
      this.props.fetchVehicle(vehicle.id);
    } else {
      this.initForm(vehicle);
    }
  };

  componentDidUpdate = (prevProps) => {
    const { vehicle } = this.props;
    if (vehicle && prevProps.vehicle !== vehicle && vehicle.last_periodic_maintenance !== undefined) {
      this.initForm(vehicle);
      if (this.state.loading) {
        this.setState({ loading: false });
      }
    }
  };

  initForm = (vehicle) => {
    const { formData } = this.state;
    const newFormData = Object.assign({}, formData);

    if (vehicle.year_model) {
      newFormData.year_model = vehicle.year_model;
    }

    if (vehicle.owner_description) {
      newFormData.owner_description = vehicle.owner_description;
    }

    if (vehicle.organization_data) {
      newFormData.organization_data.identifier = vehicle.organization_data.identifier;
      newFormData.organization_data.maintenance_interval_months = vehicle.organization_data.maintenance_interval_months;
      newFormData.organization_data.maintenance_interval_km = vehicle.organization_data.maintenance_interval_km;
      newFormData.organization_data.maintenance_interval_hours = vehicle.organization_data.maintenance_interval_hours;
      newFormData.organization_data.business_unit = vehicle.organization_data.business_unit
        ? String(vehicle.organization_data.business_unit) : '';
      newFormData.organization_data.responsible_user = vehicle.organization_data.responsible_user
        ? String(vehicle.organization_data.responsible_user) : '';
    }

    if (vehicle.last_periodic_maintenance) {
      newFormData.last_periodic_maintenance.date = vehicle.last_periodic_maintenance.date;
      newFormData.last_periodic_maintenance.km = vehicle.last_periodic_maintenance.km;
      newFormData.last_periodic_maintenance.hours = vehicle.last_periodic_maintenance.hours;
      newFormData.last_periodic_maintenance.maintenance = vehicle.last_periodic_maintenance.maintenance;
    }

    if (vehicle.organization_vehicle_status) {
      newFormData.organization_vehicle_status.employee = vehicle.organization_vehicle_status.employee;
    }

    this.setState({
      formData: newFormData
    });
  };

  setIsValid = (name, isValid) => {
    const { formValid } = this.state;
    const newFormValid = {
      ...formValid,
      [name]: isValid,
    };

    this.setState({ formValid: newFormValid });
  };

  getIsValid = () => {
    const { formValid } = this.state;
    let isValid = true;
    Object.values(formValid).forEach(fieldIsValid => {
      if (!fieldIsValid) {
        isValid = false;
      }
    });
    return isValid;
  };

  updateFormState = changedObject => {
    const {
      formData
    } = this.state;
    const newFormData = Object.assign({}, formData);
    newFormData[changedObject.target] = changedObject.value;
    this.setState({
      formData: newFormData
    });
  };

  updateOrganizationDataFormState = changedObject => {
    const {
      formData
    } = this.state;
    const newFormData = Object.assign({}, formData);
    newFormData['organization_data'][changedObject.target] = changedObject.value;
    this.setState({
      formData: newFormData
    });
  };

  updateLastPeriodicMaintenanceFormState = changedObject => {
    const {
      formData
    } = this.state;
    const newFormData = Object.assign({}, formData);
    newFormData['last_periodic_maintenance'][changedObject.target] = changedObject.value;

    // If date or km changed, clear the maintenance relation
    // The backend will try to match a new maintenance or keep it null
    if (changedObject.target === 'date' || changedObject.target === 'km') {
      newFormData['last_periodic_maintenance']['maintenance'] = null;
    }

    this.setState({
      formData: newFormData
    });
  };

  updateOrganizationVehicleStatusFormState = changedObject => {
    const {
      formData
    } = this.state;
    const newFormData = Object.assign({}, formData);
    newFormData['organization_vehicle_status'][changedObject.target] = changedObject.value;
    this.setState({
      formData: newFormData
    });
  };


  onSubmit = () => {
    const {
      vehicle,
      updateVehicle,
      updateOrganizationVehicle,
      isOrganizationVehicle,
      selectedOrganizationId,
      selectedWorkshopId,
      closeModal
    } = this.props;
    const { formData } = this.state;

    const copyFormData = Object.assign({}, formData);
    // The select values are strings; the API wants ids or null.
    copyFormData.organization_data = Object.assign({}, formData.organization_data, {
      business_unit: formData.organization_data.business_unit ? Number(formData.organization_data.business_unit) : null,
      responsible_user: formData.organization_data.responsible_user ? Number(formData.organization_data.responsible_user) : null,
    });
    // A fleet outside every chain never showed the field; leaving it out of the
    // payload keeps the API from touching it (any subset may be sent).
    if (!this.fleetBelongsToChain()) {
      delete copyFormData.organization_data.responsible_user;
    }

    if (vehicle) {
      if (isOrganizationVehicle) {
        // The fleet and workshop portals edit the model data in
        // "Muuta teknisiä tietoja", so this form does not show or send it.
        delete copyFormData.year_model;
        delete copyFormData.owner_description;
        updateOrganizationVehicle(vehicle.id, copyFormData, vehicle.organization_id, selectedWorkshopId || null);
      } else {
        delete copyFormData.organization_data;
        delete copyFormData.last_periodic_maintenance;
        delete copyFormData.organization_vehicle_status;
        updateVehicle(vehicle.id, copyFormData);
      }
    }
    closeModal();
  };

  getChoices = () => {
    const { choices, vehicle } = this.props;
    // The store holds one organization's pick-lists at a time; only trust ours.
    if (!choices || !vehicle || choices.organization_id != vehicle.organization_id) return null;
    return choices;
  };

  renderSettingsSelects = (screenSize) => {
    const { t, vehicle } = this.props;
    const { formData } = this.state;
    const choices = this.getChoices();
    const showResponsibleUser = this.fleetBelongsToChain();
    if (!this.canManageSettings() || !choices) {
      const businessUnitName = vehicle && vehicle.organization_data ? vehicle.organization_data.business_unit_name : null;
      const responsibleUserName = vehicle && vehicle.organization_data ? vehicle.organization_data.responsible_user_name : null;
      return (
        <React.Fragment>
          <p style={getStyle(screenSize, styles, 'readOnlyRow')}>
            <span className='font-titillium-web-light'>{t('businessUnitLabel')}: </span>
            <span className='font-titillium-web-semi-bold'>{businessUnitName || '-'}</span>
          </p>
          {showResponsibleUser && (
            <p style={getStyle(screenSize, styles, 'readOnlyRow')}>
              <span className='font-titillium-web-light'>{this.term('responsibleUser')}: </span>
              <span className='font-titillium-web-semi-bold'>{responsibleUserName || '-'}</span>
            </p>
          )}
        </React.Fragment>
      );
    }
    const businessUnitOptions = [{ label: t('noBusinessUnit'), value: '' }].concat(
      (choices.business_units || []).map((unit) => ({ label: unit.name, value: String(unit.id) })),
    );
    const managerOptions = [{ label: this.term('noResponsibleUser'), value: '' }].concat(
      (choices.vehicle_managers || []).map((u) => ({ label: u.name, value: String(u.id) })),
    );
    return (
      <React.Fragment>
        <CBSelectField
          name='business_unit'
          value={formData.organization_data.business_unit}
          options={businessUnitOptions}
          onChange={this.updateOrganizationDataFormState}
          labelText={t('businessUnitLabel')}
          labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
          inputStyle={getStyle(screenSize, styles, 'select')}
        />
        {showResponsibleUser && (
          <CBSelectField
            name='responsible_user'
            value={formData.organization_data.responsible_user}
            options={managerOptions}
            onChange={this.updateOrganizationDataFormState}
            labelText={this.term('responsibleUser')}
            labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
            inputStyle={getStyle(screenSize, styles, 'select')}
          />
        )}
      </React.Fragment>
    );
  };

  render() {
    const { formData, loading } = this.state;
    const {
      modalIsOpen, closeModal, afterOpenModal, t, isOrganizationVehicle, vehicle
    } = this.props;
    // Which meters this record follows decides which of the kilometre and hour
    // inputs are offered. Every registered vehicle follows kilometres, so for
    // those the dialog is unchanged.
    const tracksKm = !vehicle || vehicle.tracks_km !== false;
    const tracksHours = !!(vehicle && vehicle.tracks_hours);

    if (loading) {
      return (
        <CBModal
          modalIsOpen={modalIsOpen}
          closeModal={closeModal}
          title={this.term('vehicleTopInfoModalHeading')}
          cancelText={t('cancel')}
          submitText={t('save')}
          submitForm={function () {}}
          isValid={false}
        >
          <div style={{ position: 'relative', minHeight: 200 }}>
            <CBSpinnerFloating />
          </div>
        </CBModal>
      );
    }

    return (
      <CBMediaQuery>
        {screenSize => (
          <CBModal
            modalIsOpen={modalIsOpen}
            afterOpenModal={afterOpenModal}
            closeModal={closeModal}
            title={this.term('vehicleTopInfoModalHeading')}
            cancelText={t('cancel')}
            submitText={t('save')}
            submitForm={this.onSubmit}
            isValid={true}
            >
            {
              isOrganizationVehicle && (
                <React.Fragment>
                  <div style={getStyle(screenSize, styles, 'bodyContainer')}>
                    <p style={getStyle(screenSize, styles, 'subHeading')}>{this.term('organizationDataLabel')}</p>
                    <CBInputField
                      name='identifier'
                      inputStyle={getStyle(screenSize, styles, 'input')}
                      value={formData.organization_data.identifier}
                      onChange={this.updateOrganizationDataFormState}
                      labelText={t('identifierLabel')}
                      labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                    />
                    {this.renderSettingsSelects(screenSize)}
                    <CBInputField
                      name='employee'
                      inputStyle={getStyle(screenSize, styles, 'input')}
                      value={formData.organization_vehicle_status.employee}
                      onChange={this.updateOrganizationVehicleStatusFormState}
                      validators={[getLengthValidator(0, 21)]}
                      isValidCallback={this.setIsValid}
                      labelText={t('userLabel')}
                      labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                    />
                  </div>

                  <div style={getStyle(screenSize, styles, 'bodyContainer')}>
                    <p style={getStyle(screenSize, styles, 'subHeading')}>
                      {t('periodicMaintenanceLabel')}
                    </p>
                    <p style={getStyle(screenSize, styles, 'infoText')}>
                      {this.term('periodicMaintenanceInfo')}
                      <a href='/settings/predicted-maintenance-settings'>{t('here')}</a>
                    </p>
                    <CBInputField
                      name='maintenance_interval_months'
                      inputStyle={getStyle(screenSize, styles, 'input')}
                      value={formData.organization_data.maintenance_interval_months}
                      type='number'
                      onChange={this.updateOrganizationDataFormState}
                      labelText={t('mainentanceIntervalMonthsLabel')}
                      labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                    />
                    {
                      tracksKm && (
                        <CBInputField
                          name='maintenance_interval_km'
                          inputStyle={getStyle(screenSize, styles, 'input')}
                          value={formData.organization_data.maintenance_interval_km}
                          type='number'
                          onChange={this.updateOrganizationDataFormState}
                          labelText={t('mainentanceIntervalKmLabel')}
                          labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                        />
                      )
                    }
                    {
                      tracksHours && (
                        <CBInputField
                          name='maintenance_interval_hours'
                          inputStyle={getStyle(screenSize, styles, 'input')}
                          value={formData.organization_data.maintenance_interval_hours}
                          type='number'
                          onChange={this.updateOrganizationDataFormState}
                          labelText={t('mainentanceIntervalHoursLabel')}
                          labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                        />
                      )
                    }
                    <div style={getStyle(screenSize, styles, 'inputContainer')}>
                      <p
                        className='font-titillium-web-semi-bold'
                        style={getStyle(screenSize, styles, 'dateInputLabel')}
                      >
                        {t('lastPeriodicMaintenanceDateLabel')}
                      </p>
                      <div className='datepicker-container-white-bg'>
                        <CBDatePickerInput
                          displayedDate={formData.last_periodic_maintenance.date ? moment(formData.last_periodic_maintenance.date) : null}
                          type='date'
                          dateFormat={finnishDateFormat}
                          changeDate={(value) => {
                            this.updateLastPeriodicMaintenanceFormState({ target: 'date', value: value ? moment(value).format('YYYY-MM-DD') : null});
                          }}
                        />
                      </div>
                    </div>
                    {
                      tracksKm && (
                        <CBInputField
                          name='km'
                          inputStyle={getStyle(screenSize, styles, 'input')}
                          value={formData.last_periodic_maintenance.km}
                          type='number'
                          onChange={this.updateLastPeriodicMaintenanceFormState}
                          labelText={t('lastPeriodicMaintenanceKmLabel')}
                          labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                        />
                      )
                    }
                    {
                      tracksHours && (
                        <CBInputField
                          name='hours'
                          inputStyle={getStyle(screenSize, styles, 'input')}
                          value={formData.last_periodic_maintenance.hours}
                          type='number'
                          onChange={this.updateLastPeriodicMaintenanceFormState}
                          labelText={t('lastPeriodicMaintenanceHours')}
                          labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                        />
                      )
                    }
                  </div>
                </React.Fragment>
              )
            }

            {
              // Fleet and workshop vehicles have their model data in
              // "Muuta teknisiä tietoja"; the personal portal has no such dialog.
              !isOrganizationVehicle && (
              <div style={getStyle(screenSize, styles, 'bodyContainer')}>
                <p style={getStyle(screenSize, styles, 'subHeading')}>{t('modelDataLabel')}</p>
                <CBInputField
                  name='year_model'
                  inputStyle={getStyle(screenSize, styles, 'input')}
                  value={formData.year_model}
                  type='number'
                  placeholder={t('yearModelPlaceholder')}
                  onChange={this.updateFormState}
                  validators={[getLengthValidator(0, 4)]}
                  isValidCallback={this.setIsValid}
                  labelText={t('yearModelLabel')}
                  labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                />
                <CBInputField
                  name='owner_description'
                  inputStyle={getStyle(screenSize, styles, 'input')}
                  value={formData.owner_description}
                  placeholder={t('vehicleOwnerDescriptionPlaceholder')}
                  onChange={this.updateFormState}
                  validators={[getLengthValidator(0, 21)]}
                  isValidCallback={this.setIsValid}
                  labelText={t('vehicleOwnerDescriptionLabel')}
                  labelTextStyle={getStyle(screenSize, styles, 'inputLabelText')}
                />
              </div>
              )
            }
          </CBModal>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    dateInputLabel: {
      color: 'black',
      textAlign: 'center',
      paddingBottom: 6,
    },
    infoText: {
      color: 'black',
      fontSize: 14,
      marginBottom: 16,
    },
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 32px'
    },
    subHeading: {
      color: 'black',
      textAlign: 'center',
      fontSize: 18,
    },
    datepickerContainer: {
      display: 'flex',
    },
    dateInputLabel: {
      color: 'black',
      textAlign: 'center'
    },
    input: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      textAlign: 'center',
      resize: 'none',
      color: 'black',
      borderRadius: 100,
      height: 40,
      border: 'none',
      width: '100%',
    },
    inputLabelText: {
      color: 'black',
    },
    select: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      textAlign: 'center',
      color: 'black',
      borderRadius: 100,
      height: 40,
      border: 'none',
      width: '100%',
      textAlignLast: 'center',
    },
    readOnlyRow: {
      color: 'black',
      fontSize: 14,
      textAlign: 'center',
      margin: '8px 0',
    },
  },
  small: {
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 16px'
    }
  },
};

function mapStateToProps(state, ownProps) {
  var reduxVehicle = state.vehicle.vehicle;
  var passedVehicle = ownProps.vehicle;
  var vehicle = null;
  if (passedVehicle && reduxVehicle && reduxVehicle.id === passedVehicle.id) {
    vehicle = reduxVehicle;
  } else if (passedVehicle) {
    vehicle = passedVehicle;
  } else {
    vehicle = reduxVehicle;
  }
  return {
    vehiclePermissionRequest: state.vehiclePermission,
    user: state.authUser.user,
    vehicle: vehicle,
    selectedOrganizationId: state.organization.selectedOrganization,
    selectedWorkshopId: state.workshop.selectedWorkshop,
    choices: state.organizationVehicleSettings.organizationVehicleSettingsChoices,
  };
}

export default connect(
  mapStateToProps,
  {
    updateVehicle: _updateVehicle,
    updateOrganizationVehicle: _updateOrganizationVehicle,
    fetchVehicle: _fetchVehicle,
    fetchOrganizationVehicleSettingsChoices: _fetchOrganizationVehicleSettingsChoices,
  },
)(translate('VehicleDetailsView')(EditVehicleTopInfoModal));
